import { useEffect, useMemo, useState } from "react";
import { api, formatTRY, formatNum, toNum, downloadExport } from "@/lib/apiClient";
import { MeasureInput } from "@/components/recipes/MeasureInput";
import { groupByCategory, CategoryHeader, OwnerBadge, LockBadge, useCanEdit } from "@/lib/groupBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, ChefHat, X, Tag, Copy, TrendingUp, TrendingDown, AlertTriangle, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { keepScroll } from "@/lib/keepScroll";
import { printNutritionLabel } from "@/lib/nutritionLabel";

const MacroPill = ({ label, value, color, suffix = "g" }) => (
  <div className="rounded-lg bg-secondary/60 px-3 py-2 text-center">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="font-heading text-sm font-bold" style={{ color }}>{value}{suffix}</p>
  </div>
);

export default function Recipes() {
  const canEdit = useCanEdit();
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [size, setSize] = useState("");
  const [flavors, setFlavors] = useState([]);
  const [flavorInput, setFlavorInput] = useState("");
  const [category, setCategory] = useState("Genel");
  const [price, setPrice] = useState(0);
  const [packaging, setPackaging] = useState(0);
  const [vat, setVat] = useState(0);
  const [rows, setRows] = useState([]);
  const [lowOpen, setLowOpen] = useState(false);

  const load = () => keepScroll(() => Promise.all([
    api.get("/recipes").then((r) => setRecipes(r.data)),
    api.get("/ingredients").then((r) => setIngredients(r.data)),
  ]));
  useEffect(() => { load(); }, []);

  const ingMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const rowGrams = (r) => {
    if (r.mode === "adet") {
      const ing = ingMap[r.ingredient_id];
      return (toNum(r.quantity) || 0) * (toNum(ing?.piece_grams) || 0);
    }
    return toNum(r.grams) || 0;
  };

  const [liveFlavor, setLiveFlavor] = useState("");
  const live = useMemo(() => {
    const t = { protein: 0, carbs: 0, fat: 0, fiber: 0, calories: 0, vp: 0, cost: 0, weight: 0 };
    const active = flavors.includes(liveFlavor) ? liveFlavor : (flavors[0] || "");
    rows.forEach((r) => {
      if (r.flavor && r.flavor !== active) return;
      const ing = ingMap[r.ingredient_id];
      const g = rowGrams(r);
      if (ing) {
        const f = g / (ing.unit === "adet" ? 1 : 100);
        t.protein += ing.protein_per_100g * f;
        t.carbs += ing.carbs_per_100g * f;
        t.fat += ing.fat_per_100g * f;
        t.fiber += (ing.fiber_per_100g || 0) * f;
        t.calories += ing.calories_per_100g * f;
        t.vp += ing.vp_per_100g * f;
        t.cost += ing.cost_per_100g * f;
        t.weight += ing.unit === "adet" ? g * (toNum(ing.piece_grams) || 0) : g;
      }
    });
    const sp = toNum(price) || 0;
    const ingredientCost = t.cost;
    const vr = toNum(vat) || 0;
    const netPrice = vr > 0 ? sp / (1 + vr / 100) : sp;
    const vatAmount = sp - netPrice;
    t.cost = ingredientCost + (toNum(packaging) || 0) + vatAmount;
    const profit = sp - t.cost;
    return { ...t, ingredientCost, netPrice, vatAmount, profit, margin: sp > 0 ? (profit / sp) * 100 : 0 };
  }, [rows, ingMap, price, packaging, vat, flavors, liveFlavor]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing(null); setName(""); setSize(""); setFlavors([]); setFlavorInput(""); setCategory("Genel"); setPrice(0); setPackaging(0); setVat(0); setRows([]); setOpen(true);
  };
  const fillFrom = (r) => {
    setName(r.product_name); setSize(r.size || ""); setFlavors(r.flavors || []); setFlavorInput(""); setCategory(r.category); setPrice(r.selling_price);
    setPackaging(r.packaging_cost || 0); setVat(r.vat_rate || 0);
    setRows(r.ingredients.map((i) => ({ ingredient_id: i.ingredient_id, grams: i.grams, mode: i.mode || "g", quantity: i.quantity || "", measure: i.measure || "", flavor: i.flavor || "" })));
  };
  const openEdit = (r) => { setEditing(r.id); fillFrom(r); setOpen(true); };
  const openVariant = (r) => { setEditing(null); fillFrom(r); setSize(""); setOpen(true); toast.info("Yeni boy için ölçü/ml girin ve içerikleri ayarlayın"); };

  const addRow = (flavor = "") => setRows([...rows, { ingredient_id: ingredients[0]?.id || "", grams: 50, mode: "g", quantity: 1, measure: "", flavor }]);
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) return toast.error("Ürün adı gerekli");
    const unresolved = rows.filter((r) => r.ingredient_id && !ingMap[r.ingredient_id]).length;
    if (unresolved) return toast.error(`${unresolved} silinmiş malzeme satırı var — yerine yeni malzeme seçin veya satırı kaldırın`);
    const dup = recipes.find((r) => r.id !== editing && r.product_name.trim().toLowerCase() === name.trim().toLowerCase() && (r.size || "").trim().toLowerCase() === size.trim().toLowerCase());
    if (dup) return toast.error(size.trim() ? "Bu ürünün bu boyu zaten var" : "Aynı isimde ürün var — farklı boy için Boy/Ölçü girin");
    const payload = {
      product_name: name.trim(), size: size.trim(), flavors, category, selling_price: toNum(price) || 0,
      packaging_cost: toNum(packaging) || 0, vat_rate: toNum(vat) || 0,
      ingredients: rows.filter((r) => r.ingredient_id).map((r) => ({ ingredient_id: r.ingredient_id, grams: rowGrams(r), mode: r.mode || "g", quantity: r.mode === "adet" ? (toNum(r.quantity) || 0) : 0, measure: (r.measure || "").trim(), flavor: flavors.includes(r.flavor) ? r.flavor : "" })),
    };
    try {
      if (editing) await api.put(`/recipes/${editing}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editing ? "Ürün güncellendi" : "Ürün oluşturuldu");
      keepScroll(async () => { setOpen(false); await load(); });
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const del = async (id) => { await api.delete(`/recipes/${id}`); toast.success("Ürün silindi"); load(); };
  const exportFile = async (ext) => {
    try { await downloadExport(`/export/recipes.${ext}`, {}, `fithouse_receteler.${ext}`); toast.success(`${ext.toUpperCase()} dosyası indirildi`); }
    catch (e) { toast.error("Dışa aktarma başarısız"); }
  };

  const avg = useMemo(() => {
    const priced = recipes.filter((r) => r.selling_price > 0);
    if (!priced.length) return null;
    const profit = priced.reduce((a, r) => a + r.profit, 0) / priced.length;
    const margin = priced.reduce((a, r) => a + r.profit_margin, 0) / priced.length;
    return { profit, margin, count: priced.length, low: priced.filter((r) => r.profit_margin < margin).length };
  }, [recipes]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Ürün & Reçete Oluşturucu</h1>
          <p className="mt-1 text-sm text-muted-foreground">Malzemelerden ürün oluşturun, makro & maliyet otomatik hesaplansın</p>
        </div>
        <div className="flex items-center gap-3">
        <Button variant="secondary" data-testid="export-recipes-pdf" onClick={() => exportFile("pdf")}><FileText className="mr-1.5 h-4 w-4 text-destructive" /> PDF</Button>
        <Button variant="secondary" data-testid="export-recipes-xlsx" onClick={() => exportFile("xlsx")}><FileSpreadsheet className="mr-1.5 h-4 w-4 text-primary" /> Excel</Button>
        {avg && (
          <div className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-2" data-testid="avg-profit-box" title={`${avg.count} ürün ortalaması`}>
            <TrendingUp className="h-5 w-5 text-primary" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ort. Kâr Oranı</p>
              <p className={`font-heading text-lg font-extrabold leading-tight ${avg.margin >= 0 ? "text-primary" : "text-destructive"}`} data-testid="avg-margin">%{formatNum(avg.margin, 1)}</p>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ort. Kâr (₺)</p>
              <p className={`font-heading text-lg font-extrabold leading-tight ${avg.profit >= 0 ? "text-primary" : "text-destructive"}`} data-testid="avg-profit">{formatTRY(avg.profit)}</p>
            </div>
            {avg.low > 0 && (
              <>
                <div className="h-8 w-px bg-border" />
                <button type="button" className="rounded-lg px-1 text-left transition-colors hover:bg-destructive/10" data-testid="avg-low-btn" onClick={() => setLowOpen(true)} title="Ortalamanın altındaki ürünleri gör">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ort. Altı</p>
                  <p className="flex items-center gap-1 font-heading text-lg font-extrabold leading-tight text-destructive underline decoration-dotted underline-offset-4" data-testid="avg-low-count"><AlertTriangle className="h-4 w-4" />{avg.low}</p>
                </button>
              </>
            )}
          </div>
        )}
        <Dialog open={lowOpen} onOpenChange={setLowOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="avg-low-dialog">
            <DialogHeader><DialogTitle>Ortalamanın Altındaki Ürünler</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Genel ortalama kâr oranı <b>%{formatNum(avg?.margin || 0, 1)}</b>. Satıra tıklayınca ürün düzenleme açılır.</p>
            <div className="max-h-[60vh] divide-y divide-border overflow-y-auto">
              {recipes.filter((r) => r.selling_price > 0 && r.profit_margin < (avg?.margin || 0)).sort((a, b) => a.profit_margin - b.profit_margin).map((r) => (
                <button type="button" key={r.id} data-testid={`avg-low-row-${r.id}`} onClick={() => { setLowOpen(false); openEdit(r); }}
                  className="flex w-full items-center justify-between gap-3 py-2.5 text-left transition-colors hover:bg-secondary/40">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{r.display_name || r.product_name}</p>
                    <p className="text-xs text-muted-foreground">{r.category} · Satış {formatTRY(r.selling_price)} · Maliyet {formatTRY(r.cost_price)}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-heading font-bold text-destructive">%{formatNum(r.profit_margin, 1)}</p>
                    <p className="text-[11px] text-muted-foreground">{formatTRY(r.profit)} · ort. farkı −{formatNum((avg?.margin || 0) - r.profit_margin, 1)} puan</p>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-recipe-btn" onClick={openNew} className="font-semibold">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Ürün
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Ürünü Düzenle" : "Yeni Ürün / Reçete"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Ürün Adı</Label>
                  <Input data-testid="recipe-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Boy / Ölçü</Label>
                  <Input data-testid="recipe-size" placeholder="örn. 473 ml" value={size} onChange={(e) => setSize(e.target.value)} className="mt-1" list="size-suggestions" />
                  <datalist id="size-suggestions"><option value="473 ml" /><option value="710 ml" /><option value="Küçük" /><option value="Büyük" /></datalist>
                </div>
                <div>
                  <Label className="text-xs">Kategori</Label>
                  <Input data-testid="recipe-category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1" />
                </div>
              </div>

              <div className="rounded-xl border border-border bg-background/50 p-3">
                <Label className="text-xs">Lezzet / Çeşit Seçenekleri <span className="text-muted-foreground">(aynı fiyat — örn. Şeftali, Limon; her çeşide ayrı malzeme bağlayabilirsiniz)</span></Label>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="recipe-flavors">
                  {flavors.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary" data-testid={`recipe-flavor-${f}`}>
                      {f}
                      <button type="button" onClick={() => setFlavors(flavors.filter((x) => x !== f))} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                  <Input data-testid="recipe-flavor-input" value={flavorInput} placeholder="Lezzet yazıp Enter'a basın" className="h-8 w-56 text-xs"
                    onChange={(e) => setFlavorInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        const v = flavorInput.trim();
                        if (v && !flavors.some((x) => x.toLowerCase() === v.toLowerCase())) setFlavors([...flavors, v]);
                        setFlavorInput("");
                      }
                    }} />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-xs">Malzemeler (gram veya adet)</Label>
                  <Button size="sm" variant="secondary" onClick={() => addRow("")} data-testid="add-recipe-ingredient">
                    <Plus className="mr-1 h-3.5 w-3.5" /> Malzeme Ekle
                  </Button>
                </div>
                {flavors.length > 0 && (
                  <p className="mb-2 text-[11px] text-muted-foreground" data-testid="recipe-flavor-hint">
                    Her malzeme satırında "Çeşit" seçin: <b>Ortak</b> satırlar her çeşitte kullanılır; bir çeşide bağlı satır yalnızca o çeşit satıldığında stoktan düşer.
                  </p>
                )}
                <div className="space-y-2">
                  {rows.map((r, i) => {
                    const ing = ingredients.find((x) => x.id === r.ingredient_id);
                    const unitAdet = ing?.unit === "adet";
                    const pieceOk = !unitAdet && toNum(ing?.piece_grams) > 0;
                    const isAdet = r.mode === "adet" && pieceOk;
                    return (
                    <div key={`${r.ingredient_id || "new"}-${i}`} className={`space-y-1 ${!ing && r.ingredient_id ? "rounded-lg border border-destructive/50 bg-destructive/5 p-1.5" : ""}`}>
                    {!ing && r.ingredient_id && (
                      <p className="flex items-center gap-1 text-[11px] font-semibold text-destructive" data-testid={`recipe-ing-missing-${i}`}>
                        <AlertTriangle className="h-3 w-3" /> Bu malzeme depodan silinmiş ({formatNum(toNum(r.grams), 2)} g) — aşağıdan yenisini seçin
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <Select value={ing ? r.ingredient_id : ""} onValueChange={(v) => updateRow(i, { ingredient_id: v })}>
                        <SelectTrigger className={`flex-1 ${!ing && r.ingredient_id ? "border-destructive text-destructive" : ""}`} data-testid={`recipe-ing-select-${i}`}><SelectValue placeholder={!ing && r.ingredient_id ? "Yeni malzeme seç…" : "Malzeme seç"} /></SelectTrigger>
                        <SelectContent>
                          {ingredients.map((ing) => (
                            <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {flavors.length > 0 && (
                        <Select value={flavors.includes(r.flavor) ? r.flavor : "__all__"} onValueChange={(v) => updateRow(i, { flavor: v === "__all__" ? "" : v })}>
                          <SelectTrigger className={`w-32 ${r.flavor ? "border-primary/60 text-primary" : ""}`} data-testid={`recipe-ing-flavor-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">Ortak (tümü)</SelectItem>
                            {flavors.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      <Input type="text" inputMode="decimal" value={isAdet ? r.quantity : r.grams} className="w-20"
                        data-testid={`recipe-ing-grams-${i}`}
                        onChange={(e) => updateRow(i, isAdet ? { quantity: e.target.value } : { grams: e.target.value })} />
                      {unitAdet ? (
                        <span className="w-[4.5rem] text-center text-[11px] font-semibold text-muted-foreground" data-testid={`recipe-ing-mode-${i}`}>adet</span>
                      ) : (
                      <div className="flex rounded-md bg-secondary/60 p-0.5" data-testid={`recipe-ing-mode-${i}`}>
                        {[["g", ing?.unit || "g"], ["adet", "adet"]].map(([m, l]) => (
                          <button key={m} type="button" disabled={m === "adet" && !pieceOk}
                            title={m === "adet" && !pieceOk ? "Malzemede '1 Adet / Ölçek Gramajı' tanımlı değil" : ""}
                            data-testid={`recipe-ing-mode-${m}-${i}`}
                            onClick={() => updateRow(i, { mode: m, quantity: r.quantity || 1 })}
                            className={`rounded px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-30 ${(isAdet ? "adet" : "g") === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                        ))}
                      </div>
                      )}
                      <span className="w-16 text-right font-mono text-[11px] text-muted-foreground" data-testid={`recipe-ing-equiv-${i}`}>
                        {unitAdet ? (toNum(ing?.piece_grams) > 0 ? `≈ ${formatNum(rowGrams(r) * ing.piece_grams, 0)} g` : "") : isAdet ? `= ${formatNum(rowGrams(r), 0)} ${ing?.unit || "g"}` : pieceOk ? `≈ ${formatNum(rowGrams(r) / ing.piece_grams, 1)} adet` : ""}
                      </span>
                      {ing && (() => { const f = rowGrams(r) / (unitAdet ? 1 : 100); return (
                        <span className="w-28 shrink-0 text-right font-mono text-[11px] leading-tight" data-testid={`recipe-ing-costvp-${i}`}>
                          <span className="block font-semibold text-foreground">{formatTRY(ing.cost_per_100g * f)}</span>
                          <span className="block" style={{ color: "var(--chart-5)" }}>{formatNum(ing.vp_per_100g * f, 2)} VP</span>
                        </span>
                      ); })()}
                      <Button size="icon" variant="ghost" onClick={() => removeRow(i)}><X className="h-4 w-4" /></Button>
                    </div>
                    <MeasureInput index={i} value={r.measure} onChange={(v) => updateRow(i, { measure: v })} />
                    </div>
                    );
                  })}
                  {rows.length === 0 && <p className="text-xs text-muted-foreground">Henüz malzeme eklenmedi</p>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 rounded-xl border border-border bg-background/50 p-3">
                {flavors.length > 0 && (
                  <div className="col-span-3 flex flex-wrap items-center gap-1.5" data-testid="recipe-live-flavor">
                    <span className="text-[11px] text-muted-foreground">Hesaplanan çeşit:</span>
                    {flavors.map((f) => (
                      <button key={f} type="button" data-testid={`recipe-live-flavor-${f}`} onClick={() => setLiveFlavor(f)}
                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${(flavors.includes(liveFlavor) ? liveFlavor : flavors[0]) === f ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:text-foreground"}`}>{f}</button>
                    ))}
                  </div>
                )}
                <MacroPill label="Protein" value={formatNum(live.protein)} color="var(--chart-1)" />
                <MacroPill label="Karb." value={formatNum(live.carbs)} color="var(--chart-2)" />
                <MacroPill label="Yağ" value={formatNum(live.fat)} color="var(--chart-3)" />
                <MacroPill label="Lif" value={formatNum(live.fiber)} color="var(--chart-2)" />
                <MacroPill label="Kalori" value={formatNum(live.calories, 0)} color="var(--chart-4)" suffix="" />
                <MacroPill label="VP" value={formatNum(live.vp)} color="var(--chart-5)" suffix="" />
                <MacroPill label="Ağırlık" value={formatNum(live.weight, 0)} color="var(--foreground)" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Satış Fiyatı ₺ (KDV dahil)</Label>
                  <Input data-testid="recipe-price" type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Bardak / Ambalaj ₺</Label>
                  <Input data-testid="recipe-packaging" type="text" inputMode="decimal" value={packaging} onChange={(e) => setPackaging(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">KDV %</Label>
                  <Input data-testid="recipe-vat" type="text" inputMode="decimal" value={vat} onChange={(e) => setVat(e.target.value)} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 rounded-xl border border-border bg-background/50 p-3 text-sm" data-testid="recipe-cost-summary">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam Maliyet</p>
                  <p className="font-mono font-semibold">{formatTRY(live.cost)}</p>
                  <p className="text-[11px] text-muted-foreground">Malzeme {formatTRY(live.ingredientCost)} + Ambalaj {formatTRY(toNum(packaging))}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">KDV Hariç Fiyat</p>
                  <p className="font-mono font-semibold">{formatTRY(live.netPrice)}</p>
                  <p className="text-[11px] text-muted-foreground">KDV %{formatNum(toNum(vat) || 0, 0)} ({formatTRY(live.vatAmount)})</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Net Kâr (Marj)</p>
                  <p className={`font-mono font-semibold ${live.profit >= 0 ? "text-primary" : "text-destructive"}`} data-testid="recipe-profit">
                    {formatTRY(live.profit)} ({formatNum(live.margin, 0)}%)
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-recipe-btn" onClick={save} className="font-semibold">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {groupByCategory(recipes, "product_name").map((g) => {
          const priced = g.items.filter((r) => r.selling_price > 0);
          const cm = priced.length ? priced.reduce((a, r) => a + r.profit_margin, 0) / priced.length : null;
          const cp = priced.length ? priced.reduce((a, r) => a + r.profit, 0) / priced.length : 0;
          const weak = cm !== null && avg && cm < avg.margin;
          return [
          <CategoryHeader key={`h-${g.category}`} title={g.category} count={g.items.length} suffix="ürün"
            extra={cm !== null && (
              <span data-testid={`category-avg-${g.category}`} title={weak ? "Genel ortalamanın altında" : "Genel ortalamanın üzerinde"}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${weak ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`}>
                {weak ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />} ort. kâr %{formatNum(cm, 0)} · {formatTRY(cp)}
              </span>
            )} />,
          ...g.items.map((r) => {
          const low = avg && r.selling_price > 0 && r.profit_margin < avg.margin;
          return (
          <div key={r.id} data-testid={`recipe-card-${r.id}`} className={`animate-fade-up rounded-xl border bg-card p-5 transition-all ${low ? "border-destructive/50 hover:border-destructive" : "border-border hover:border-primary/50"}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold">
                  {r.product_name}
                  {r.size && <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 align-middle text-xs font-semibold text-primary" data-testid={`recipe-size-${r.id}`}>{r.size}</span>}
                </h3>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{r.category} <OwnerBadge ownerId={r.owner_id} /> <LockBadge item={r} /></p>
                {r.ingredients.some((i) => i.missing) && (
                  <button type="button" data-testid={`recipe-missing-${r.id}`} disabled={!canEdit(r)} onClick={() => openEdit(r)}
                    title="Düzenle ve yerine yeni malzeme seç"
                    className="mt-1 inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold text-destructive hover:bg-destructive/25 disabled:opacity-60">
                    <AlertTriangle className="h-3 w-3" /> {r.ingredients.filter((i) => i.missing).length} silinmiş malzeme — onar
                  </button>
                )}
                {r.flavors?.length > 0 && <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`recipe-flavors-${r.id}`}>Lezzetler: {r.flavors.join(" · ")}</p>}
                {r.flavor_stock?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`recipe-flavor-stock-${r.id}`}>
                    {r.flavor_stock.map((fs) => (
                      <span key={fs.flavor} data-testid={`recipe-flavor-stock-${r.id}-${fs.flavor}`}
                        title={fs.limiting ? `Sınırlayan malzeme: ${fs.limiting}` : ""}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${fs.low ? "bg-destructive/15 text-destructive" : "bg-secondary/60 text-muted-foreground"}`}>
                        {fs.low && <AlertTriangle className="h-3 w-3" />}{fs.flavor}: {fs.units ?? "—"} porsiyon
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" title="Farklı boy ekle (kopyala)" data-testid={`variant-recipe-${r.id}`} onClick={() => openVariant(r)}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" title="Besin Etiketi" data-testid={`label-recipe-${r.id}`}
                  onClick={() => { if (!printNutritionLabel(r)) toast.error("Açılır pencere engellendi"); }}>
                  <Tag className="h-4 w-4 text-primary" />
                </Button>
                <Button size="icon" variant="ghost" data-testid={`edit-recipe-${r.id}`} disabled={!canEdit(r)} onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" data-testid={`delete-recipe-${r.id}`} disabled={!canEdit(r)} onClick={() => del(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-6 gap-1.5 text-center">
              {[["P", r.total_protein, "var(--chart-1)"], ["K", r.total_carbs, "var(--chart-2)"], ["Y", r.total_fat, "var(--chart-3)"], ["Lif", r.total_fiber, "var(--chart-2)"], ["Kcal", r.total_calories, "var(--chart-4)"], ["VP", r.total_vp, "var(--chart-5)"]].map(([l, v, c]) => (
                <div key={l} className="rounded-md bg-secondary/50 py-1.5">
                  <p className="text-[9px] uppercase text-muted-foreground">{l}</p>
                  <p className="font-mono text-xs font-bold" style={{ color: c }}>{formatNum(v, l === "Kcal" ? 0 : 1)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
              <div>
                <p className="text-xs text-muted-foreground">
                  Maliyet {formatTRY(r.cost_price)}
                  {r.packaging_cost > 0 && <span> · Ambalaj {formatTRY(r.packaging_cost)}</span>}
                  {r.vat_rate > 0 && <span> · KDV %{formatNum(r.vat_rate, 0)} ({formatTRY(r.vat_amount)})</span>}
                </p>
                <p className="font-heading text-lg font-extrabold text-primary">{formatTRY(r.selling_price)}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.profit < 0 || low ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`} data-testid={`recipe-margin-${r.id}`}
                title={low ? `Ortalamanın (%${formatNum(avg.margin, 0)}) altında` : undefined}>
                {low && <AlertTriangle className="mr-1 inline h-3 w-3" />}Kâr {formatTRY(r.profit)} · %{formatNum(r.profit_margin, 0)}
              </span>
            </div>
          </div>
          );
          }),
        ];
        })}
        {recipes.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
            <ChefHat className="mx-auto mb-2 h-8 w-8 opacity-40" /> Henüz ürün oluşturulmadı
          </div>
        )}
      </div>
    </div>
  );
}
